import { cn } from '../cn';

import type { HTMLAttributes, ReactNode } from 'react';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode | undefined;
  subtitle?: ReactNode | undefined;
  /** Buttons or menus shown on the right of the header. */
  actions?: ReactNode | undefined;
  /** 24 px inner padding. Turn off for tables and media that bleed to the edge. */
  padded?: boolean | undefined;
  /** shadow-3 instead of shadow-2: use for hoverable or clickable cards. */
  elevated?: boolean | undefined;
}

/** Containers use --radius-lg and a soft elevation; one hairline under the header at most. */
export function Card({
  title,
  subtitle,
  actions,
  padded = true,
  elevated = false,
  className,
  children,
  ...rest
}: CardProps) {
  const header =
    title || actions ? (
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          {title ? (
            <div className="text-base leading-6 font-semibold text-content-default">{title}</div>
          ) : null}
          {subtitle ? (
            <div className="text-[13px] leading-[18px] text-content-subdued">{subtitle}</div>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
    ) : null;
  return (
    <div
      className={cn(
        'box-border flex min-w-0 flex-col rounded-lg bg-surface-static-default font-display text-content-default',
        elevated ? 'shadow-3' : 'shadow-2',
        padded && 'p-6',
        className,
      )}
      {...rest}
    >
      {header}
      {children}
    </div>
  );
}
